import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { bumpUnread } from "@/components/chat/UnreadBadge";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { BackgroundPicker, GroupMessageDialog, chatBackgroundStyle } from "@/components/ChatExtras";
import { ThreadList } from "@/components/chat/ThreadList";
import { MessageBubble } from "@/components/chat/MessageBubble";
import { GroupThreadDialog } from "@/components/chat/GroupThreadDialog";
import { GroupMembersDialog } from "@/components/chat/GroupMembersDialog";
import { NewMessageDialog } from "@/components/chat/NewMessageDialog";
import { Composer } from "@/components/chat/Composer";
import { CompanyTag } from "@/components/CompanySwitcher";

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/threads");
      setThreads(data);
      setActiveId((cur) => cur || (data.length > 0 ? data[0].id : cur));
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  const loadMessages = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/threads/${id}/messages`);
      setMsgs(data);
      bumpUnread(); // opening a thread marks it read → refresh the sidebar badge
      setTimeout(() => { scrollRef.current?.scrollTo({ top: 999999 }); }, 50);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    if (!activeId) return;
    loadMessages(activeId);
    const iv = setInterval(() => loadMessages(activeId), 5000);
    return () => clearInterval(iv);
  }, [activeId, loadMessages]);

  useEffect(() => {
    api.get("/messaging/recipients").then(({ data }) => setCustomers(data)).catch((err) => console.error("Failed to load recipients:", err));
  }, [user.id]);

  const refresh = async () => { await loadMessages(activeId); await loadThreads(); };
  const activeThread = threads.find((t) => t.id === activeId);

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="chat-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="tag-mono text-blue-700 mb-1">MESSAGES</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold font-heading text-slate-900">{isAdmin ? "Customer Threads" : "Support Chat"}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && <GroupThreadDialog recipients={customers} onCreated={async (threadId) => { await loadThreads(); setActiveId(threadId); }} />}
          {isAdmin && <GroupMessageDialog customers={customers.filter((c) => c.role !== "admin")} onSent={loadThreads} />}
          <NewMessageDialog isAdmin={isAdmin} customers={customers} onSent={async (threadId) => { await loadThreads(); setActiveId(threadId); }} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 h-[calc(100dvh-170px)] lg:h-[calc(100vh-180px)]">
        <div className={`${activeThread ? "hidden lg:block" : ""} min-h-0`}><ThreadList threads={threads} activeId={activeId} onSelect={setActiveId} /></div>
        <Card className={`${activeThread ? "flex" : "hidden lg:flex"} flex-col overflow-hidden min-h-0`}>
          {!activeThread ? (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">Select a conversation</div>
          ) : (
            <>
              <div className="px-3 sm:px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Button variant="ghost" size="icon" className="lg:hidden shrink-0 -ml-1" onClick={() => setActiveId(null)} aria-label="Back to conversations" data-testid="chat-back-button"><ArrowLeft className="h-5 w-5" /></Button>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-2">{activeThread.other_user?.name}<CompanyTag customerId={activeThread.company_id} testid="chat-header-company" /></div>
                    <div className="text-xs text-slate-500 truncate">{activeThread.is_group ? `${activeThread.members?.length + 1} people · ${activeThread.members?.map((m) => m.name).join(", ")}, you` : `${activeThread.other_user?.company || ""}${activeThread.other_user?.company ? " · " : ""}${activeThread.other_user?.email}${activeThread.team_size > 1 ? ` · ${activeThread.team_size} people on their team can reply` : ""}`}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin && activeThread.is_group && <GroupMembersDialog thread={activeThread} recipients={customers} meId={user.id} onChanged={refresh} />}
                  <BackgroundPicker thread={activeThread} onChanged={loadThreads} />
                </div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 sm:p-5 space-y-3 bg-slate-50/50" style={chatBackgroundStyle(activeThread)} data-testid="messages-container">
                {msgs.length === 0 && <div className="text-sm text-slate-400 text-center py-10">Start the conversation.</div>}
                {msgs.map((m) => <MessageBubble key={m.id} m={m} mine={m.sender_id === user.id} isAdmin={isAdmin} customerId={activeThread.other_user?.id} userId={user.id} showSender={!!activeThread.is_group} onChanged={refresh} />)}
              </div>
              <Composer threadId={activeId} onSent={refresh} />
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
